import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, Globe, Receipt, LifeBuoy, LogOut,
  Cpu, Sparkles, MessageCircle, Repeat, User, Wallet, ImagePlus,
  Search, Hash, Bot, Sparkles as SparklesIcon
} from "lucide-react";
import { useAuth } from "../../lib/auth";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/dashboard/projects", label: "Projects", icon: FolderKanban },
  { to: "/dashboard/assistance", label: "AI Assistance", icon: MessageCircle },
  { to: "/dashboard/sites", label: "My Sites", icon: Globe },
  { to: "/dashboard/orders", label: "Orders & Invoices", icon: Receipt },
  { to: "/dashboard/subscriptions", label: "Subscriptions", icon: Repeat },
  { to: "/dashboard/credits", label: "AI Credits", icon: Wallet },
  { to: "/dashboard/images", label: "AI Image Studio", icon: ImagePlus },
  { to: "/dashboard/seo/analyzer", label: "Website Analyzer", icon: Search },
  { to: "/dashboard/seo/keywords", label: "Keyword Research", icon: Hash },
  { to: "/dashboard/seo/assistant", label: "AI SEO Assistant", icon: Bot },
  { to: "/dashboard/seo/suite", label: "SEO Suite", icon: SparklesIcon },
  { to: "/dashboard/ai", label: "AI Jobs", icon: Cpu },
  { to: "/dashboard/tickets", label: "Support", icon: LifeBuoy },
  { to: "/dashboard/account", label: "My Account", icon: User },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!user) nav("/login?next=/dashboard"); }, [user, nav]);
  if (!user) return null;

  const initials = user.name.split(" ").map(n=>n[0]).slice(0,2).join("").toUpperCase();

  return (
    <div className="min-h-[calc(100vh-64px)] grid grid-cols-12 gap-0">
      <aside className="col-span-12 lg:col-span-2 border-r border-slate-800 bg-slate-900/50">
        <div className="p-5 lg:sticky lg:top-[64px]">
          {/* User card */}
          <div className="card p-4 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">{initials}</div>
              <div className="min-w-0 flex-1">
                <div className="text-white font-semibold truncate text-sm">{user.name}</div>
                <div className="text-slate-500 text-xs truncate">{user.email}</div>
              </div>
            </div>
          </div>

          <nav className="space-y-1" data-testid="dashboard-nav">
            {NAV.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                className={({isActive}) => `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-blue-600 text-white shadow-[0_6px_20px_-6px_rgba(37,99,235,0.6)]" : "text-slate-300 hover:bg-slate-800"}`}
                data-testid={`dashboard-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
              >
                <n.icon size={15}/> {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="divider my-4"/>
          <div className="space-y-1">
            <Link to="/ai-builder" className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-blue-300 hover:bg-blue-500/10">
              <Sparkles size={15}/> New AI Website
            </Link>
            <Link to="/ai-app-builder" className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-blue-300 hover:bg-blue-500/10">
              <Sparkles size={15}/> New AI App
            </Link>
            <Link to="/ai-tools" className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-blue-300 hover:bg-blue-500/10">
              <MessageCircle size={15}/> AI Chat
            </Link>
          </div>

          <div className="divider my-4"/>
          <button onClick={()=>{logout(); nav("/");}} className="w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-rose-300 hover:bg-rose-500/10" data-testid="logout-btn">
            <LogOut size={15}/> Sign Out
          </button>
        </div>
      </aside>

      <main className="col-span-12 lg:col-span-10 p-6 lg:p-8 max-w-[1400px]">
        <Outlet />
      </main>
    </div>
  );
}
